/**
 * Content helpers — shared between TypographyV2 and its sub-components.
 * These are pure functions with zero MUI dependencies.
 */

/**
 * Checks if a given node is an empty text node (empty paragraph).
 * @param {Object} node
 * @returns {boolean}
 */
export const isEmptyTextNode = (node) => {
  return (
    typeof node.nodeType === 'string' &&
    node.nodeType.match(/paragraph/i) &&
    node.content?.length === 1 &&
    node.content[0].nodeType === 'text' &&
    node.content[0].value === ''
  )
}

/**
 * Contentful RichText appends an empty paragraph after elements like headings
 * and lists when they are the last item. This strips that trailing node.
 * @param {Array} content
 * @returns {Array}
 */
export const removeTrailingReturnChar = (content) => {
  if (!Array.isArray(content) || content.length <= 1) return content

  const lastItem = content[content.length - 1]
  const prevLastItem = content[content.length - 2]

  if (isEmptyTextNode(lastItem) && !isEmptyTextNode(prevLastItem)) {
    return content.slice(0, -1)
  }
  return content
}

/**
 * Filters out block-level elements when inline mode is used to prevent hydration errors.
 * @param {Array} content
 * @param {boolean} inline
 * @returns {Array}
 */
export const filterInlineContent = (content, inline) => {
  if (!inline || !Array.isArray(content)) return content

  const blockElements = [
    'heading-1',
    'heading-2',
    'heading-3',
    'heading-4',
    'heading-5',
    'heading-6',
    'unordered-list',
    'ordered-list',
    'list-item',
    'hr',
    'table',
    'table-row',
    'table-cell',
    'table-header-cell',
    'blockquote',
    'embedded-asset',
  ]

  return content.filter((item) => {
    if (item.nodeType && blockElements.includes(item.nodeType)) {
      console.warn(`TypographyV2: Skipping block-level element "${item.nodeType}" in inline mode`)
      return false
    }
    return true
  })
}

// ── Contentful link resolution helpers ──────────────────────────────────────

export const replaceInlineEntries = (obj, links) => {
  if (obj?.content?.length) {
    return { ...obj, content: obj.content.map((sub) => replaceInlineEntries(sub, links)) }
  }
  for (const inlineItem of links.entries.inline) {
    if (
      inlineItem?.sys?.id === obj?.data?.target?.sys?.id ||
      inlineItem?.id === obj?.data?.target?.sys?.id
    ) {
      return { ...obj, data: { ...inlineItem, id: inlineItem?.sys?.id } }
    }
  }
  return obj
}

export const replaceBlockEntries = (obj, links) => {
  if (Array.isArray(obj?.content)) {
    return { ...obj, content: obj.content.map((sub) => replaceBlockEntries(sub, links)) }
  }
  for (const blockItem of links.entries.block) {
    if (
      blockItem?.sys?.id === obj?.data?.target?.sys?.id ||
      blockItem?.id === obj?.data?.target?.sys?.id
    ) {
      return { ...obj, data: { ...blockItem, id: blockItem?.sys?.id } }
    }
  }
  return obj
}

export const replaceInlineHyperLink = (obj, links) => {
  if (obj?.data?.target?.sys?.id) {
    for (const hl of links.assets.hyperlink) {
      if (hl?.sys?.id === obj?.data?.target?.sys?.id || hl?.id === obj?.data?.target?.sys?.id) {
        return { ...obj, data: { ...hl, id: hl?.sys?.id } }
      }
    }
  }
  if (obj?.content?.length) {
    return { ...obj, content: obj.content.map((sub) => replaceInlineHyperLink(sub, links)) }
  }
  return obj
}

/**
 * Normalises the many shapes that `content` can arrive in into a
 * consistent `{ textContent }` shape for `documentToReactComponents`.
 * @param {Object} content
 * @param {boolean} inline
 * @returns {{ textContent: Object }}
 */
export const handleContentToMapper = (content = {}, inline = false) => {
  let textContent = {}

  if (Object.hasOwnProperty.call(content || {}, 'json')) {
    textContent = content.json

    if (content?.links?.entries?.inline?.length) {
      textContent = replaceInlineEntries(textContent, content.links)
    }
    if (content?.links?.assets?.hyperlink?.length) {
      textContent = replaceInlineHyperLink(textContent, content.links)
    }
    if (content?.links?.entries?.block?.length) {
      textContent = replaceBlockEntries(textContent, content.links)
    }
  }

  if (Object.hasOwnProperty.call(content || {}, 'fields')) {
    textContent = content.fields.textContent
  } else if (Object.hasOwnProperty.call(content || {}, 'nodeType')) {
    textContent = content
  }

  textContent.content = removeTrailingReturnChar(textContent.content)
  textContent.content = filterInlineContent(textContent.content, inline)

  return { textContent }
}

/**
 * Parse \n inside rich-text text values into <br /> elements
 * and strip Unicode line/paragraph separators.
 * @param {Array} children
 * @returns {Array}
 */
export const parseRichTextLineBreak = (children) => {
  if (Array.isArray(children) && children.length === 1 && children[0] === '') {
    return ['\u00A0']
  }

  function splitBreakLineInline(inputArray) {
    if (!Array.isArray(inputArray)) return inputArray

    const result = []
    inputArray.forEach((item, itemIndex) => {
      if (typeof item === 'string') {
        if (item.includes('\n')) {
          const parts = item.split('\n')
          parts.forEach((part, index) => {
            result.push(part)
            if (index < parts.length - 1) {
              result.push(<br key={`br-${itemIndex}-${index}`} />)
            }
          })
        } else {
          result.push(item)
        }
      } else {
        result.push(item)
      }
    })
    return result
  }

  return splitBreakLineInline(children).map((child) =>
    typeof child === 'string' ? child.replaceAll(/[\u2028\u2029]/g, '') : child
  )
}
